import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { makeRequest } from "../../utils/request";
import { User } from "./userSlice";
import { organizationActions } from "./organizationSlice";

type ResetPasswordPayload = {
  email: string;
};

type SignInPayload = {
  email?: string;
  password?: string;
  inviteId?: string;
};

type SignUpPayload = {
  email?: string;
  password?: string;
};

export type SessionState = {
  user: User | null;
  error: string | null;
  isLoading: boolean;
  isLoadingSession: boolean;
  languageCode?: string;
  selectedOrganizationId?: string; // For admin users to switch organizations
  invite?: {
    id: string;
    email?: string;
  };
};

const initialState: SessionState = {
  user: null,
  error: null,
  isLoading: false,
  isLoadingSession: true,
  languageCode: "",
  selectedOrganizationId: typeof window !== "undefined" ? localStorage.getItem("selectedOrganizationId") || undefined : undefined,
};

const getSession = createAsyncThunk("session/getSession", async (_, { dispatch }) => {
  const response = await makeRequest<{ data: User }>("/v1/auth/session");
  const user = response.data || {};

  // Skip org fetch for unverified users (would 403)
  if (user?.emailVerified !== false) {
    if (user?.isAdmin) {
      // Ensure admin's own org is loaded first so RequireAuth doesn't block
      dispatch(organizationActions.getById({ id: user?.organizationId }));
      // Fetch all organizations for admin users with agent counts for dropdown sorting
      dispatch(organizationActions.get({ includeAgentCount: "true" }));
    } else {
      // Fetch only user's organization for regular users (no need for agent count)
      dispatch(organizationActions.getById({ id: user?.organizationId }));
    }
  }

  return user;
});

const signIn = createAsyncThunk("session/signin", async (payload: SignInPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signin", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signUp = createAsyncThunk("session/signup", async (payload: SignUpPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signup", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signOut = createAsyncThunk("session/signout", async () => {
  await makeRequest("/v1/auth/signout", {
    method: "POST",
  });
});

const resetPassword = createAsyncThunk("session/resetPassword", async (payload: ResetPasswordPayload) => {
  await makeRequest("/v1/auth/reset", {
    method: "POST",
    body: payload,
  });
});

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setInvite: (state, action) => {
      state.invite = action.payload;
    },
    setSelectedOrganization: (state, action) => {
      state.selectedOrganizationId = action.payload;
      // Persist to localStorage so it survives page reloads
      if (action.payload) {
        localStorage.setItem("selectedOrganizationId", action.payload);
      } else {
        localStorage.removeItem("selectedOrganizationId");
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSession.fulfilled, (state, action) => {
      state.isLoadingSession = false;
      state.user = action.payload;
    });

    builder.addCase(getSession.rejected, (state) => {
      state.isLoadingSession = false;
      state.user = null;
    });

    builder.addCase(signIn.rejected, (state) => {
      state.error = "Invalid email or password";
    });

    builder.addCase(signOut.fulfilled, (state) => {
      state.isLoading = false;
      state.user = null;
    });

    builder.addCase(signUp.rejected, (state, action) => {
      state.error = action.error.message || null;
    });

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/pending"),
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/rejected"),
      (state) => {
        state.isLoading = false;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/fulfilled"),
      (state) => {
        state.isLoading = false;
      },
    );
  },
});

export const sessionReducer = sessionSlice.reducer;

export const sessionActions = {
  ...sessionSlice.actions,
  getSession,
  resetPassword,
  signIn,
  signUp,
  signOut,
};
